import classNames from "classnames";
import { motion } from "framer-motion";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import { ReactComponent as IconMinus } from "@Icons/minus.svg";
import { ReactComponent as IconPlus } from "@Icons/plus.svg";
import { ReactComponent as IconQuote } from "@Icons/quote.svg";
import { Tile } from "@Types/pagify";
import { getLink } from "@Utils/pagify";
import "./style.scss";

const collapsedTextBlockHeight = 150;

export const SeoDescription: FunctionComponent<{
  tiles: Tile[];
}> = ({ tiles }) => {
  return (
    <div className="SeoDescription">
      {tiles.map((tile) => (
        <SeoDescriptionItem key={tile.slug} tile={tile} />
      ))}
    </div>
  );
};

const SeoDescriptionItem: FunctionComponent<{
  tile: Tile;
}> = ({ tile }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [displayMoreButton, setDisplayMoreButton] = useState<boolean>(true);

  const textBlockRef = useRef<HTMLDivElement>(null);

  const intl = useIntl();
  const localeSlug = intl.locale;

  useEffect(() => {
    const textBlockHeight = textBlockRef.current?.getBoundingClientRect().height;
    if (textBlockHeight && textBlockHeight > collapsedTextBlockHeight) {
      setDisplayMoreButton(true);
    } else {
      setDisplayMoreButton(false);
    }
  }, [textBlockRef]);

  const handleClickMore = () => {
    setOpen(!open);
  };

  return (
    <div
      className="SeoDescriptionItem"
      key={tile.slug}
      style={{
        backgroundColor: tile.backgroundColor ? tile.backgroundColor.hex : "",
        borderStyle: tile.backgroundColor && tile.backgroundColor.hex ? "none" : "dashed",
      }}
    >
      <IconQuote />
      <motion.div
        animate={open ? "open" : "collapsed"}
        initial={"collapsed"}
        variants={{
          collapsed: { height: collapsedTextBlockHeight, overflow: "hidden" },
          open: { height: "auto" },
        }}
      >
        <div className="SeoDescriptionItem-content" ref={textBlockRef}>
          {!!tile.textBlock1 &&
            tile.textBlock1.map((textBlock) => {
              return textBlock.navigationLink?.link ? (
                <a
                  className="SeoDescriptionItem-content-link"
                  key={textBlock.text}
                  href={getLink(textBlock.navigationLink, localeSlug)}
                >
                  {textBlock.text}
                </a>
              ) : (
                <span className="SeoDescriptionItem-content-text" key={textBlock.text}>
                  {textBlock.text}
                </span>
              );
            })}
        </div>
      </motion.div>
      {displayMoreButton && (
        <div
          className={classNames("SeoDescriptionItem-more", {
            "SeoDescriptionItem-more--open": open,
          })}
        >
          <ButtonSmily
            icon={open ? <IconMinus /> : <IconPlus />}
            iconPosition="alone"
            onClick={handleClickMore}
            type="secondary"
            size="s"
          />
        </div>
      )}
    </div>
  );
};
